import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function AdTemplates() {
  const [tpls, setTpls] = useState([]);
  const [assets, setAssets] = useState([]);
  const [name, setName] = useState("");
  const [selectedAds, setSelectedAds] = useState([]);

  const load = () => api.get("/ad-templates").then((r) => setTpls(r.data));

  useEffect(() => {
    load();
    api.get("/assets", { params: { type: "ad" } }).then((r) => setAssets(r.data));
  }, []);

  const create = async () => {
    if (!name || selectedAds.length === 0) {
      toast.error("Give it a name and add at least one ad");
      return;
    }
    const slots = selectedAds.map((id, i) => {
      const a = assets.find((x) => x.id === id);
      return { asset_id: id, duration_sec: a?.duration_sec || 30, order: i };
    });
    await api.post("/ad-templates", {
      name,
      slots,
      total_duration_sec: slots.reduce((a, b) => a + b.duration_sec, 0),
    });
    setName("");
    setSelectedAds([]);
    toast.success("Template created");
    load();
  };

  const del = async (id) => {
    await api.delete(`/ad-templates/${id}`);
    load();
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Ad Templates</div>
        <h1 className="font-display font-bold text-2xl mt-1">Break templates with SCTE-35</h1>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="panel">
          <div className="panel-header"><span className="panel-title">Existing templates</span></div>
          <div className="p-3 space-y-2">
            {tpls.map((t) => (
              <div key={t.id} className="border border-zinc-800 rounded-sm p-3 bg-zinc-950" data-testid={`ad-template-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display font-bold text-sm">{t.name}</div>
                    <div className="text-[10px] font-mono text-zinc-500">
                      {t.slots.length} slots · {t.total_duration_sec}s · SCTE-35 aligned
                    </div>
                  </div>
                  <button onClick={() => del(t.id)} className="text-zinc-500 hover:text-red-400" data-testid={`ad-template-del-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {t.slots.map((s, i) => {
                    const a = assets.find((x) => x.id === s.asset_id);
                    return (
                      <span key={i} className="text-[10px] font-mono border border-zinc-700 rounded-sm px-1.5 py-0.5">
                        {a?.name || s.asset_id.slice(0, 6)} · {s.duration_sec}s
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <div className="panel-header"><span className="panel-title">New template</span></div>
          <div className="p-3 space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Template name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Prime time 60s"
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm"
                data-testid="ad-template-name"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Add ad slot</Label>
              <Select onValueChange={(v) => setSelectedAds((p) => [...p, v])}>
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="ad-template-add-ad">
                  <SelectValue placeholder="Pick an ad asset…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {assets.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} · {a.duration_sec}s</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="border border-zinc-800 rounded-sm p-2 min-h-[80px]">
              {selectedAds.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">No slots yet</div>
              ) : (
                <div className="space-y-1">
                  {selectedAds.map((id, i) => {
                    const a = assets.find((x) => x.id === id);
                    return (
                      <div key={i} className="flex items-center justify-between text-xs font-mono">
                        <span>#{i + 1} · {a?.name} · {a?.duration_sec}s</span>
                        <button onClick={() => setSelectedAds((p) => p.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <Button onClick={create} className="tenant-btn rounded-sm w-full" data-testid="ad-template-create">
              <Plus className="w-4 h-4 mr-1" /> Create template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
